import {KeyboardEvent, useMemo, useState} from 'react';
import {Box, Input, List, Tag, Wrap, WrapItem} from '@chakra-ui/react';

interface TagListInputProps {
    value: string[];
    onChange: (next: string[]) => void;
    suggestions: string[];
    placeholder?: string;
    disabled?: boolean;
}

/** A lightweight tag input with an optional filtered-suggestion dropdown — no extra combobox dependency needed. */
export function TagListInput({value, onChange, suggestions, placeholder, disabled = false}: TagListInputProps) {
    const [input, setInput] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredSuggestions = useMemo(() => {
        const query = input.trim().toLowerCase();
        return suggestions
            .filter((name) => !value.includes(name))
            .filter((name) => !query || name.toLowerCase().includes(query))
            .slice(0, 8);
    }, [input, suggestions, value]);

    function addName(name: string) {
        const trimmed = name.trim();
        if (!trimmed || value.includes(trimmed)) return;
        onChange([...value, trimmed]);
        setInput('');
        setIsOpen(false);
    }

    function removeName(name: string) {
        onChange(value.filter((n) => n !== name));
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            e.preventDefault();
            addName(input);
        } else if (e.key === 'Backspace' && input === '' && value.length > 0) {
            removeName(value[value.length - 1]);
        }
    }

    return <Box position="relative">
        {value.length > 0 &&
            <Wrap mb={2} gap={2} opacity={disabled ? 0.5 : undefined}>
                {value.map((name) => (
                    <WrapItem key={name}>
                        <Tag.Root colorPalette="blue">
                            <Tag.Label>{name}</Tag.Label>
                            {!disabled &&
                                <Tag.EndElement>
                                    <Tag.CloseTrigger aria-label="close" onClick={() => removeName(name)}/>
                                </Tag.EndElement>
                            }
                        </Tag.Root>
                    </WrapItem>
                ))}
            </Wrap>
        }
        <Input
            value={input}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
                setInput(e.target.value);
                setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 150)}
            onKeyDown={handleKeyDown}
        />
        {isOpen && !disabled && filteredSuggestions.length > 0 &&
            <List.Root
                variant="plain"
                position="absolute"
                zIndex={10}
                bg="white"
                borderWidth="1px"
                borderRadius="md"
                mt={1}
                w="100%"
                maxH="180px"
                overflowY="auto"
                boxShadow="md"
            >
                {filteredSuggestions.map((name) => (
                    <List.Item
                        key={name}
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{bg: 'gray.100'}}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addName(name)}
                    >
                        {name}
                    </List.Item>
                ))}
            </List.Root>
        }
    </Box>;
}
